import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import { BanksService } from '../../../banks/services/banks.service';
import { SupervisorsService } from '../../../supervisors/services/supervisors.service';
import { UserRegistrationsService } from '../../services/user-registrations.service';
import {
  BankSummaryDto,
  BranchSummaryDto,
} from '../../../../core/data/new-dtos/bank.dtos';
import {
  UserRegistrationAssignmentDto,
  UserRegistrationResponseDto,
} from '../../../../core/data/new-dtos/user-registration.dtos';

export interface RegistrationApprovalResult {
  action: 'approve' | 'reject' | 'cancel';
  comments?: string;
  reason?: string;
  assignments?: UserRegistrationAssignmentDto[];
}

interface AssignmentRow {
  bankId: number | null;
  branchId: number | null;
  branches: BranchSummaryDto[];
}

@Component({
  selector: 'app-registration-approval-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './registration-approval-modal.component.html',
})
export class RegistrationApprovalModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() registration!: UserRegistrationResponseDto;
  @Input() isVisible = false;
  @Output() closed = new EventEmitter<RegistrationApprovalResult>();

  detail?: UserRegistrationResponseDto;
  loading = true;
  comments = '';
  reason = '';
  rejecting = false;

  banks: BankSummaryDto[] = [];
  rows: AssignmentRow[] = [];
  /** Branch ids the approver may assign; empty means unrestricted (super user). */
  private allowedBranchIds: number[] = [];

  constructor(
    private userRegs: UserRegistrationsService,
    private banksService: BanksService,
    private supervisorsService: SupervisorsService,
    private auth: AuthService,
    private elementRef: ElementRef,
  ) { }

  get isSupervisorRequest(): boolean {
    return String(this.detail?.requestedRole ?? this.registration?.requestedRole)
      .toLowerCase()
      .includes('supervisor');
  }

  get canEditAssignments(): boolean {
    return !this.auth.isManager;
  }

  /**
   * Approving turns the applicant into a bank employee, and an employee without a
   * branch has no scope: they would see nothing and no supervisor would own them.
   * Every row on show has to name both a bank and a branch before that can happen.
   */
  get assignmentsComplete(): boolean {
    // Managers get no assignment editor at all; the API keeps what was requested
    if (!this.canEditAssignments) return true;

    return this.rows.length > 0 && this.rows.every((r) => !!r.bankId && !!r.branchId);
  }

  async ngOnInit() {
    await this.auth.profileReady();
    await this.loadScope();
    await this.loadDetail();
  }

  ngAfterViewInit(): void {
    document.body.appendChild(this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    const element = this.elementRef.nativeElement;
    if (element?.parentNode === document.body) {
      document.body.removeChild(element);
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.onCancel();
    }
  }

  onCancel(): void {
    this.closed.emit({ action: 'cancel' });
  }

  onApprove(): void {
    // The button is disabled in this state; this guards the keyboard and any future caller
    if (!this.assignmentsComplete) return;

    const assignments = this.rows.map((r) => ({
      bankId: r.bankId!,
      branchId: r.branchId!,
    }));

    this.closed.emit({
      action: 'approve',
      comments: this.comments,
      assignments: assignments.length ? assignments : undefined,
    });
  }

  onReject(): void {
    if (!this.reason.trim()) return;
    this.closed.emit({ action: 'reject', reason: this.reason.trim() });
  }

  addRow(): void {
    this.rows.push({ bankId: null, branchId: null, branches: [] });
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
  }

  async onRowBankChange(row: AssignmentRow): Promise<void> {
    row.branchId = null;
    row.branches = row.bankId ? await this.loadBranches(row.bankId) : [];
  }

  /** A branch outside the approver's own scope would be refused by the API. */
  isBranchAllowed(branchId: number): boolean {
    return this.allowedBranchIds.length === 0 || this.allowedBranchIds.includes(branchId);
  }

  private async loadScope(): Promise<void> {
    try {
      if (this.auth.isSupervisor && this.auth.azureId) {
        const branches = await lastValueFrom(
          this.supervisorsService.getBranches(this.auth.azureId),
        );
        this.banks = [...new Map(branches.map((b) => [b.bank.id, b.bank])).values()];
        this.allowedBranchIds = branches.map((b) => b.branchId);
      } else if (this.auth.isDirector) {
        // Directors act within one bank; regional scope is enforced per branch
        const bankId = this.auth.bankProfile?.bankId;
        const banks = await lastValueFrom(this.banksService.getBanks());
        this.banks = banks.filter((b) => b.id === bankId);
        this.allowedBranchIds = this.auth.branchIds;
      } else if (this.auth.isManager) {
        this.allowedBranchIds = this.auth.branchIds;
      } else {
        this.banks = await lastValueFrom(this.banksService.getBanks());
      }
    } catch {
      this.banks = [];
    }
  }

  private async loadDetail(): Promise<void> {
    try {
      this.detail = await lastValueFrom(this.userRegs.getById(this.registration.id));
    } catch {
      this.detail = this.registration;
    } finally {
      await this.buildRows();
      this.loading = false;
    }
  }

  private async buildRows(): Promise<void> {
    const existing = (this.detail?.assignments ?? []).filter((a) => a.branchId);

    if (existing.length) {
      this.rows = await Promise.all(
        existing.map(async (a) => ({
          bankId: a.bankId,
          branchId: a.branchId!,
          branches: await this.loadBranches(a.bankId),
        })),
      );
    } else if (this.detail?.bankId) {
      // Only supervisors apply through `assignments`. A retailer or manager names a
      // single bank and branch on the request itself, so reading assignments alone left
      // the approver looking at empty selects under the values the applicant chose.
      this.rows = [
        {
          bankId: this.detail.bankId,
          branchId: this.detail.branchId ?? null,
          branches: await this.loadBranches(this.detail.bankId),
        },
      ];
    } else {
      this.rows = [];
    }

    if (this.rows.length === 0 && this.canEditAssignments) {
      this.addRow();
    }
  }

  private async loadBranches(bankId: number): Promise<BranchSummaryDto[]> {
    try {
      return (await lastValueFrom(this.banksService.getBranches(bankId))) ?? [];
    } catch {
      return [];
    }
  }
}
